import React, { useState, useEffect, useCallback } from 'react';
import { Percent, Trash2, Zap } from 'lucide-react';
import { colors, API, fmt, authHeaders } from '../utils/theme';
import {
  PageHeader, SearchBar, Select, Table, Th, Td, TrHover, Badge,
  Modal, FormField, Input, TextArea, Button, LoadingSpinner, EmptyState,
  ConfirmDialog,
} from '../components/UI';

const TIPOS_ENCARGO = [
  { id: 'JUROS_ROTATIVO',     label: 'Juros Rotativo' },
  { id: 'JUROS_PARCELAMENTO', label: 'Juros Parcelamento' },
  { id: 'JUROS_MORA',         label: 'Juros de Mora' },
  { id: 'IOF',                label: 'IOF' },
  { id: 'MULTA_ATRASO',       label: 'Multa por Atraso' },
  { id: 'TAXA_SERVICO',       label: 'Taxa de Serviço' },
  { id: 'OUTROS',             label: 'Outros' },
];

const tipoLabel = (tipo) => TIPOS_ENCARGO.find(t => t.id === tipo)?.label || tipo;
const tipoColor = (tipo) => ({
  JUROS_ROTATIVO: colors.danger, JUROS_PARCELAMENTO: colors.warning,
  JUROS_MORA: '#f97316', IOF: colors.info, MULTA_ATRASO: '#dc2626',
  TAXA_SERVICO: colors.textMuted, OUTROS: colors.textDim,
}[tipo] || colors.textMuted);

export default function CriseEncargosPage() {
  const mesAtual = new Date().toISOString().slice(0, 7);
  const [autoEncargos, setAutoEncargos]   = useState([]);
  const [resumoMeses,  setResumoMeses]    = useState([]);
  const [encargos,     setEncargos]       = useState([]);   // manuais
  const [cartoes,      setCartoes]        = useState([]);
  const [loading,      setLoading]        = useState(true);
  const [filtros,      setFiltros]        = useState({ search: '', mes: mesAtual, titular_id: '' });
  const [titulares,    setTitulares]      = useState([]);
  const [modalOpen,    setModalOpen]      = useState(false);
  const [deleteId,     setDeleteId]       = useState(null);
  const [saving,       setSaving]         = useState(false);
  const [form,         setForm]           = useState({
    cartao_id: '', mes_referencia: '', tipo: 'JUROS_ROTATIVO',
    valor: '', taxa_pct: '', base_calculo: '', observacoes: '',
  });

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (filtros.mes) p.set('mes', filtros.mes);
    if (filtros.titular_id) p.set('titular_id', filtros.titular_id);
    try {
      const [auto, resumo, manual] = await Promise.all([
        fetch(`${API}/crise/custos/encargos?${p}`).then(r => r.json()),
        fetch(`${API}/crise/custos/encargos/resumo?meses=6`).then(r => r.json()),
        fetch(`${API}/crise/encargos?${p}`).then(r => r.json()),
      ]);
      setAutoEncargos(Array.isArray(auto) ? auto : []);
      setResumoMeses(Array.isArray(resumo) ? resumo : []);
      setEncargos(Array.isArray(manual) ? manual : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filtros]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetch(`${API}/cartoes`, { headers: authHeaders() }).then(r => r.json()).then(d => setCartoes(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/titulares`, { headers: authHeaders() }).then(r => r.json()).then(d => setTitulares((d.data||d||[]).filter(t => t.total_cartoes > 0))).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.cartao_id || !form.mes_referencia) return alert('Cartão e mês são obrigatórios');
    setSaving(true);
    try {
      await fetch(`${API}/crise/encargos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      setModalOpen(false); load();
    } catch (e) { alert('Erro: ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    await fetch(`${API}/crise/encargos/${deleteId}`, { method: 'DELETE' });
    setDeleteId(null); load();
  };

  // Filtro de busca nos automáticos
  const autoFiltrado = autoEncargos.filter(r => {
    const q = filtros.search.toLowerCase();
    return !q || r.nome_cartao?.toLowerCase().includes(q) || r.titular_nome?.toLowerCase().includes(q)
      || r.descricao?.toLowerCase().includes(q) || r.banco_nome?.toLowerCase().includes(q);
  });

  const totalAuto = autoFiltrado.reduce((s, r) => s + (r.valor || 0), 0);
  const totalManual = encargos.reduce((s, r) => s + (r.valor || 0), 0);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Encargos e Juros"
        subtitle={`${autoFiltrado.length} lançamentos detectados automaticamente`}
        onAdd={() => setModalOpen(true)} addLabel="+ Manual" />

      {/* Resumo por mês */}
      {resumoMeses.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {resumoMeses.slice(0, 6).map(m => (
            <div key={m.mes_referencia} style={{
              background: colors.card, borderRadius: 10, padding: '10px 14px',
              border: `1px solid ${filtros.mes === m.mes_referencia ? colors.danger : colors.border}`,
              cursor: 'pointer', minWidth: 120,
            }} onClick={() => setFiltros(f => ({ ...f, mes: f.mes === m.mes_referencia ? '' : m.mes_referencia }))}>
              <div style={{ fontSize: 11, color: colors.textMuted }}>{m.mes_referencia}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: colors.danger }}>{fmt(m.total)}</div>
              <div style={{ fontSize: 10, color: colors.textDim, marginTop: 2 }}>
                {m.juros > 0 && <span>J:{fmt(m.juros)} </span>}
                {m.iof > 0 && <span>IOF:{fmt(m.iof)} </span>}
                {m.multas > 0 && <span>M:{fmt(m.multas)}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        {[
          { label: 'Total automático', value: fmt(totalAuto), color: colors.danger, sub: `${autoFiltrado.length} lançamentos` },
          { label: 'Total manual', value: fmt(totalManual), color: colors.warning, sub: `${encargos.length} registros` },
          { label: 'Total combinado', value: fmt(totalAuto + totalManual), color: '#dc2626', sub: 'período filtrado' },
        ].map((kpi, i) => (
          <div key={i} style={{ background: colors.card, borderRadius: 12, padding: 16, border: `1px solid ${colors.border}`, flex: '1 1 160px' }}>
            <div style={{ fontSize: 11, color: colors.textMuted }}>{kpi.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
            <div style={{ fontSize: 11, color: colors.textDim }}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <SearchBar value={filtros.search} onChange={e => setFiltros(f => ({ ...f, search: e.target.value }))}
        placeholder="Buscar cartão, titular, descrição...">
        <Select value={filtros.titular_id} onChange={e => setFiltros(f => ({ ...f, titular_id: e.target.value }))} style={{ width: 'auto', minWidth: 160 }}>
          <option value="">Todos Titulares</option>
          {titulares.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </Select>
        <Input type="month" value={filtros.mes} onChange={e => setFiltros(f => ({ ...f, mes: e.target.value }))} style={{ width: 'auto', minWidth: 160 }} />
        <button onClick={() => setFiltros(f => ({ ...f, mes: '' }))}
          style={{ background: 'none', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 10px', color: colors.textMuted, cursor: 'pointer', fontSize: 12 }}>
          Todos meses
        </button>
      </SearchBar>

      {/* Tabela automática */}
      <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Zap size={14} color={colors.warning} />
        <span style={{ fontSize: 12, fontWeight: 600, color: colors.warning }}>Detectado automaticamente das faturas importadas — todos os cartões</span>
      </div>

      {autoFiltrado.length === 0 ? (
        <EmptyState message="Nenhum encargo/juro detectado nas faturas importadas para este período" icon={Percent} />
      ) : (
        <Table>
          <thead><tr>
            <Th>Mês</Th><Th>Data</Th><Th>Cartão</Th><Th>Titular</Th><Th>Banco</Th>
            <Th>Descrição</Th><Th>Tipo</Th><Th>Valor</Th>
          </tr></thead>
          <tbody>
            {autoFiltrado.map(r => (
              <TrHover key={r.id}
                style={{ borderLeft: r.gerenciamento_crise === 'SIM' ? '3px solid #f97316' : undefined }}>
                <Td style={{ fontSize: 11, color: colors.textMuted }}>{r.mes_referencia}</Td>
                <Td style={{ fontSize: 11, color: colors.textMuted }}>{r.data_compra || '-'}</Td>
                <Td style={{ fontWeight: 600, fontSize: 12 }}>
                  {r.nome_cartao}
                  {r.gerenciamento_crise === 'SIM' && (
                    <span style={{ fontSize: 9, background: '#f9731622', color: '#f97316', borderRadius: 3, padding: '1px 4px', marginLeft: 4 }}>CRISE</span>
                  )}
                </Td>
                <Td style={{ fontSize: 12 }}>{r.titular_nome}</Td>
                <Td style={{ fontSize: 11, color: colors.textMuted }}>{r.banco_nome || '-'}</Td>
                <Td style={{ fontSize: 12, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.descricao}>{r.descricao}</Td>
                <Td><Badge text={tipoLabel(r.tipo_classificado)} color={tipoColor(r.tipo_classificado)} /></Td>
                <Td style={{ fontWeight: 700, color: colors.danger }}>{fmt(r.valor)}</Td>
              </TrHover>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: `2px solid ${colors.border}` }}>
              <td colSpan={7} style={{ padding: '8px 12px', fontWeight: 600, color: colors.textMuted, fontSize: 12 }}>Total</td>
              <td style={{ padding: '8px 12px', fontWeight: 700, color: colors.danger }}>{fmt(totalAuto)}</td>
            </tr>
          </tfoot>
        </Table>
      )}

      {/* Encargos manuais */}
      {encargos.length > 0 && (
        <>
          <div style={{ marginTop: 24, marginBottom: 8, fontSize: 12, fontWeight: 600, color: colors.textMuted }}>
            Registros manuais
          </div>
          <Table>
            <thead><tr>
              <Th>Cartão</Th><Th>Mês</Th><Th>Tipo</Th><Th>Valor</Th><Th>Taxa</Th><Th align="center">Ações</Th>
            </tr></thead>
            <tbody>
              {encargos.map(e => (
                <TrHover key={e.id}>
                  <Td style={{ fontWeight: 600 }}>{e.nome_cartao}</Td>
                  <Td>{e.mes_referencia}</Td>
                  <Td><Badge text={tipoLabel(e.tipo)} color={tipoColor(e.tipo)} /></Td>
                  <Td style={{ color: colors.danger, fontWeight: 600 }}>{fmt(e.valor)}</Td>
                  <Td>{e.taxa_pct ? `${e.taxa_pct}%` : '-'}</Td>
                  <Td style={{ textAlign: 'center' }}>
                    <button onClick={() => setDeleteId(e.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.danger }}>
                      <Trash2 size={14} />
                    </button>
                  </Td>
                </TrHover>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {/* Modal manual */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Registrar Encargo Manual" width={680}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FormField label="Cartão" required>
            <Select value={form.cartao_id} onChange={e => setForm(f => ({ ...f, cartao_id: e.target.value }))}>
              <option value="">Selecione...</option>
              {cartoes.map(c => <option key={c.id} value={c.id}>{c.nome_cartao}</option>)}
            </Select>
          </FormField>
          <FormField label="Mês de Referência" required>
            <Input type="month" value={form.mes_referencia} onChange={e => setForm(f => ({ ...f, mes_referencia: e.target.value }))} />
          </FormField>
          <FormField label="Tipo">
            <Select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
              {TIPOS_ENCARGO.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </Select>
          </FormField>
          <FormField label="Valor (R$)">
            <Input type="number" step="0.01" value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} />
          </FormField>
          <FormField label="Taxa (%)">
            <Input type="number" step="0.01" value={form.taxa_pct} onChange={e => setForm(f => ({ ...f, taxa_pct: e.target.value }))} />
          </FormField>
          <FormField label="Base de Cálculo">
            <Input type="number" step="0.01" value={form.base_calculo} onChange={e => setForm(f => ({ ...f, base_calculo: e.target.value }))} />
          </FormField>
        </div>
        <FormField label="Observações">
          <TextArea value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} rows={2} />
        </FormField>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete}
        title="Excluir Encargo" message="Tem certeza?" />
    </div>
  );
}
